import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const actionLabels = {
  'user.created': 'User created',
  'user.updated': 'User updated',
  'user.deleted': 'User deleted',
  'user.login': 'User logged in',
  'user.logout': 'User logged out',
  'tenant.created': 'Tenant created',
  'tenant.updated': 'Tenant updated',
  'tenant.suspended': 'Tenant suspended',
  'api_key.created': 'API key created',
  'api_key.deleted': 'API key deleted',
  'webhook.created': 'Webhook created',
  'webhook.deleted': 'Webhook deleted',
  'subscription.updated': 'Subscription updated',
  'login': 'Logged in',
  'logout': 'Logged out',
  'role_changed': 'Role changed',
  'paas.created': 'Platform created',
  'paas.updated': 'Platform updated',
  'paas.deleted': 'Platform deleted',
  'saas.created': 'SaaS created',
  'saas.updated': 'SaaS updated'
};

export default function ActivityLogs({ limit = 10, tenantId: propTenantId }) {
  const params = useParams();
  const urlTenantId = params.tenantId;
  const tenantId = propTenantId || urlTenantId;
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, [tenantId]);

  // Polling for real-time activity log updates
  useEffect(() => {
    const pollLogs = setInterval(() => {
      fetchLogs();
    }, 15000); // 15 seconds

    return () => {
      clearInterval(pollLogs);
    };
  }, [tenantId]);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      let res;
      if (tenantId) {
        // Tenant-specific logs
        res = await api.get('/activity-logs', {
          headers: { 'x-tenant-id': tenantId },
          params: { limit }
        });
        setLogs(res.data.data?.logs || []);
      } else {
        // Global logs for super admin
        res = await api.get('/activity-logs/global', {
          params: { limit }
        });
        setLogs(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setError(err.response?.data?.message || 'Failed to load activity');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500 p-4">Loading activity...</div>;
  }

  if (error) {
    return <div className="text-gray-500 p-4">{error}</div>;
  }

  if (!tenantId && logs.length === 0) {
    return <div className="text-gray-500 p-4">No recent activity to display</div>;
  }

  if (logs.length === 0) {
    return <div className="text-gray-500 p-4">No recent activity</div>;
  }

  return (
    <div className="space-y-2 max-h-96 overflow-y-auto">
      {logs.map((log, index) => (
        <div key={log.logId || index} className="flex items-start gap-3 text-sm p-2 hover:bg-gray-50 rounded">
          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${getActionColor(log.action)}`} />
          <div className="flex-1 min-w-0">
            <div className="text-gray-800">
              <span className="font-medium">{actionLabels[log.action] || log.action || 'Unknown action'}</span>
              {log.entityType && <span className="text-gray-500"> - {log.entityType}</span>}
            </div>
            <div className="text-gray-500 text-xs">
              {log.userEmail || log.performedBy || 'System'}
              {log.ipAddress && ` from ${log.ipAddress}`}
            </div>
          </div>
          <div className="text-gray-400 text-xs flex-shrink-0">
            {log.createdAt ? new Date(log.createdAt).toLocaleString() : 'Unknown time'}
          </div>
        </div>
      ))}
    </div>
  );
}

function getActionColor(action) {
  if (!action) return 'bg-gray-400';
  if (action.includes('created') || action.includes('login')) return 'bg-green-500';
  if (action.includes('deleted') || action.includes('logout')) return 'bg-red-500';
  if (action.includes('updated') || action.includes('changed')) return 'bg-yellow-500';
  return 'bg-gray-400';
}